import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="sobre-nos" className="py-12 md:py-20 bg-white" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={`max-w-6xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#010E31] mb-4 md:mb-6">
              Sobre Nós
            </h2>
            <p className="text-sm md:text-base text-gray-600 italic mb-6 md:mb-8 leading-relaxed">
              — Quando a qualidade é prioridade em cada projeto, o resultado é a satisfação total do cliente. A Alumi Innova preza pela excelência no trabalho e no acabamento, conquistando destaque e consolidando sua atuação no mercado de esquadrias de alumínio em Hortolândia – SP. A empresa conta com profissionais de ótimo nível técnico, com ampla atuação na área, o que garante conhecimento especializado, atenção aos detalhes e a construção de relações sólidas baseadas em confiança e comprometimento, tratando cada cliente como único e especial.
            </p>
            <div className="w-16 md:w-24 h-1 bg-[#C0C0C0] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center mb-8 md:mb-12">
            <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/ee4ab35536c43b833838f71f4d3b75cf/f720c3f7cc40afcf494139ccc42d2df8.jpeg"
                  alt="Profissional com esquadria"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <h3 className="text-2xl md:text-3xl font-bold text-[#010E31] mb-3 md:mb-4">
                Nossa Missão
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                Temos um compromisso com você: analisar e executar cada projeto com transparência e comprometimento, garantindo sempre a melhor qualidade na produção da sua <strong>esquadrias de alumínio</strong>.
              </p>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Nossa equipe especializada trabalha com dedicação para transformar seus projetos em realidade, buscando constantemente a excelência em cada detalhe.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            <div className={`order-2 md:order-1 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h3 className="text-2xl md:text-3xl font-bold text-[#010E31] mb-3 md:mb-4">
                Nossa Experiência
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                Com profissionais com grande nível técnico e anos de experiência no mercado, nos consolidamos como referência em <strong>esquadrias de alumínio, vidros temperados, guarda-corpo, espelhos, coberturas, fachada pele de vidro, breeze e painel ripado</strong> na região de Campinas, Paulínia e toda região do Estado de São Paulo.
              </p>
              <ul className="space-y-2 md:space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Profissionais qualificados e experientes</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Materiais de alta qualidade</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Atendimento personalizado</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Garantia e suporte pós-venda</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Projetos personalizados</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Instalação rápida e eficiente</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Preços competitivos</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-check-line text-[#C0C0C0] text-lg md:text-xl"></i>
                  </div>
                  <span className="text-sm md:text-base text-gray-700">Compromisso com prazos</span>
                </li>
              </ul>
            </div>

            <div className={`order-1 md:order-2 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/ee4ab35536c43b833838f71f4d3b75cf/1dbf2b8487f06c296f6dd710ec9a4c4b.jpeg"
                  alt="Escada moderna"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
